/* eslint-disable react/prop-types */
import * as React from 'react';

import { useMemoizedFn } from 'ahooks';
import { Col, Row } from 'antd';
import { format } from 'date-fns-jalali';

import {
  PrimitiveItemSeoAdvanced,
  PrimitiveItemSeoDescription,
  PrimitiveItemSeoIndex,
  PrimitiveItemSeoOpenGraph,
  PrimitiveItemSeoTitle,
} from 'components';
import type { FileOwnerType, Seo } from 'types';
import { generateDownloadFile } from 'utils';

interface FieldType {
  seoTitle?: string;
  seoDescription?: string;
  seoOpenGraphImage?: string;
  seoIndex?: boolean;
  seoAdvance?: boolean;
}

interface PageSeoInfoProps
  extends StrictOmit<React.ComponentPropsWithoutRef<typeof Row>, 'children'> {
  seo: Seo | undefined;
  ownerType: FileOwnerType | undefined;
  defaultName?: string;
  onUpdateSeoInfo: (result: Seo) => void;
}

const PageSeoInfo = React.forwardRef<
  React.ElementRef<typeof Row>,
  PageSeoInfoProps
>(({ seo, ownerType, defaultName, onUpdateSeoInfo, ...props }, ref) => {
  const {
    title = '',
    description = '',
    openGraphImageId,
    isIndex = false,
    isAdvance = false,
  } = seo ?? {};

  const formatTemplateValue = useMemoizedFn(
    (value: string | undefined, defaultResult: string = '') => {
      if (!value) return defaultResult;

      let formattedValue = value;

      // format value with {info-name} and {date-yyyy}
      if (isAdvance) {
        formattedValue = value.replaceAll(
          /{(info|date)-([^{}]+)}/gm,
          (_substring, type, value) => {
            if (type === 'info') {
              if (value === 'name') return defaultName || defaultResult;
            }

            if (type === 'date') {
              try {
                return format(new Date(), value || defaultResult);
              } catch (error) {
                console.error(error);
              }
            }

            return defaultResult;
          },
        );
      }

      // clean value from multiple spaces after formatting
      const cleanedValue = formattedValue.trim().replaceAll(/\s+/gm, ' ');

      return cleanedValue;
    },
  );

  const onChangeSeoInfo = useMemoizedFn(
    <T extends keyof Seo>(key: T, value: Seo[T]) => {
      const result: Seo = {
        title,
        description,
        openGraphImageId,
        isIndex,
        isAdvance,
      };

      onUpdateSeoInfo({ ...result, [key]: value });
    },
  );

  return (
    <Row ref={ref} {...props}>
      <Col span={24}>
        <PrimitiveItemSeoTitle<FieldType>
          name='seoTitle'
          initialValue={title}
          helpText={formatTemplateValue(title)}
          onDebounceChange={value => {
            onChangeSeoInfo('title', value);
          }}
        />
      </Col>
      <Col span={24}>
        <PrimitiveItemSeoDescription<FieldType>
          name='seoDescription'
          initialValue={description}
          helpText={formatTemplateValue(description)}
          onDebounceChange={value => {
            onChangeSeoInfo('description', value);
          }}
        />
      </Col>
      <Col span={24}>
        <PrimitiveItemSeoOpenGraph<FieldType>
          name='seoOpenGraphImage'
          uploadProps={{
            ownerType,
            fileList:
              openGraphImageId && ownerType
                ? [
                    {
                      uid: openGraphImageId,
                      name: 'تصویر رسانه‌ای',
                      status: 'done',
                      url: generateDownloadFile(ownerType, openGraphImageId),
                    },
                  ]
                : undefined,
            onChange: ({ file: { status, response } }) => {
              onChangeSeoInfo('openGraphImageId', undefined);

              if (status === 'done' && response) {
                onChangeSeoInfo('openGraphImageId', response.id);
              }
            },
          }}
        />
      </Col>
      <Col span={24}>
        <PrimitiveItemSeoIndex<FieldType>
          name='seoIndex'
          initialValue={isIndex}
          onDebounceChange={value => {
            onChangeSeoInfo('isIndex', value);
          }}
        />
      </Col>
      <Col span={24}>
        <PrimitiveItemSeoAdvanced<FieldType>
          name='seoAdvance'
          initialValue={isAdvance}
          onDebounceChange={value => {
            onChangeSeoInfo('isAdvance', value);
          }}
        />
      </Col>
    </Row>
  );
});
PageSeoInfo.displayName = 'PageSeoInfo';

export { PageSeoInfo };
